import { useState, useDeferredValue, useMemo } from "react";
import { PageContainer } from "components/core/basic/PageContainer";
import BreadcrumbDates from "components/core/blocks/BreadcrumbDates";
import { DatesProvider, useDates } from "dates/DatesContext";
import { REPORT_LINKS } from "utils/dashboardLinks";
import { type SearchQueryPerformanceV4Row } from "./api/v4SearchQueryPerformanceReportByQuery";
import { useQuery } from "@tanstack/react-query";
import { createColumnBuilder } from "components/table/columnBuilder";
import { useAppSelector } from "redux/hooks";
import { css } from "../../../styled-system/css";
import KeywordCell from "components/custom/category/advertisements/KeywordCell";
import moment from "moment";
import MetricsCard from "components/custom/merchandise/MetricsCard";
import { useStandardEntityMetrics } from "views/merchandise/tables/metricUtils";
import { usePersistedTableState } from "components/table/usePersistedTableState";
import { idsToVisibilityState } from "components/table/utils";
import { buildMetric } from "views/merchandise/metrics";
import { COMPARE_METRIC_LOOKUP } from "views/merchandise/tables/metricsByEntity";
import { useGetV4SearchQueryPerformanceTotals } from "./api/v4SearchQueryPerformanceReportTotals";
import SearchQueryPerformanceDashboardTable from "./SearchQueryPerformanceDashboardTable";
import { Flex } from "../../../styled-system/jsx";
import { useHistory } from "react-router-dom";
import { Button } from "components/core/basic/Button";
import { Modal } from "components/core/basic/RACModal";
import { ProductSelector } from "components/core/blocks/ProductSelector/ProductSelector";
import { useGetV4SearchQueryPerformanceByDate } from "./api/v4SearchQueryPerformanceByDate";

const columnHelper = createColumnBuilder<SearchQueryPerformanceV4Row>();

const SQPRReportBody = () => {
  const [selectedAsins, setSelectedAsins] = useState<{ product_id: string; asin: string; sku: string | null }[]>([]);
  const [tempSelectedAsins, setTempSelectedAsins] = useState<
    { product_id: string; asin: string; sku: string | null }[]
  >([]);
  const [showAsinFilter, setShowAsinFilter] = useState(false);

  const history = useHistory();

  const { group, setGroup, start, end } = useDates();

  if (group !== "week") {
    setGroup("week");
  }

  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);

  const standardMetricIds = useStandardEntityMetrics("searchQuery");

  const columns = useMemo(
    () => [
      columnHelper.accessor("search_query", {
        id: "search_query",
        header: `Search Query`,
        enableHiding: false,
        minSize: 100,
        size: 300,
        cell: ({ getValue }) => {
          return (
            <div className={css({ paddingLeft: "small" })}>
              <KeywordCell value={getValue()} columnWidth={300} />
            </div>
          );
        },
      }),
      ...standardMetricIds.map((metricId) => {
        return columnHelper.metric(metricId);
      }),
    ],
    [standardMetricIds],
  );

  // Below is to hide additional metrics by default without having to define each one
  const defaultSelectedColumns = [
    "search_query",
    "search_query_score",
    "search_query_volume",
    "impressions_total",
    "impressions_brand_or_asin",
    "impressions_brand_or_asin_percent",
    "clicks_total",
    "cart_adds_brand_or_asin",
    "cart_adds_same_day_shipping_speed",
    "cart_adds_1d_shipping_speed",
    "cart_adds_2d_shipping_speed",
    "clicks_brand_or_asin",
    "clicks_same_day_shipping_speed",
    "clicks_1d_shipping_speed",
    "clicks_2d_shipping_speed",
  ];

  const defaultHiddenCols = idsToVisibilityState(
    defaultSelectedColumns,
    columns.filter((column) => Boolean(column.id)).map((column) => column.id as string),
  );

  const initialStateConfig = {
    columnVisibility: {
      ...defaultHiddenCols,
    },
    columnOrder: defaultSelectedColumns,
    columnPinning: {
      left: ["search_query"],
    },
    sorting: [{ id: "search_query_volume", desc: true }],
  };

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: `searchQueryPerformanceReport`,
    initialState: initialStateConfig,
  });

  const deferredSearch = useDeferredValue(state.globalFilter);

  const filters = useMemo(() => {
    /*
    const asinFilter =
      selectedAsins.length ?
        [
          {
            id: "asin",
            value: { op: "anyof", value: selectedAsins.map((asin) => asin.asin) },
          },
        ]
      : [];
*/

    return [
      ...state.columnFilters,
      // ...asinFilter
      //
    ];
  }, [
    state.columnFilters,
    // selectedAsins
  ]);

  const byDateOptionsGetter = useGetV4SearchQueryPerformanceByDate();
  const searchQueryPerformanceByDateReportQuery = useQuery(
    byDateOptionsGetter({
      filters,
      search: deferredSearch,
    }),
  );

  const totalOptionsGetter = useGetV4SearchQueryPerformanceTotals();
  const searchQueryPerformanceTotalsReportQuery = useQuery(
    totalOptionsGetter({
      filters,
      search: deferredSearch,
    }),
  );

  const metrics = useMemo(() => {
    const data = {
      total:
        searchQueryPerformanceTotalsReportQuery.data?.data ? searchQueryPerformanceTotalsReportQuery.data.data : {},
    };
    return standardMetricIds.map((metricId) =>
      buildMetric({ marketPlace: marketPlace }, data, metricId, COMPARE_METRIC_LOOKUP[metricId]),
    );
  }, [standardMetricIds, marketPlace, searchQueryPerformanceTotalsReportQuery.data?.data]);

  return (
    <PageContainer withBreadcrumb>
      <MetricsCard
        id={"searchQueryPerformanceReport"}
        title={"Performance Overview"}
        data={{
          date:
            searchQueryPerformanceByDateReportQuery.data?.data ?
              searchQueryPerformanceByDateReportQuery.data.data.map((row) => ({
                ...row,
                start_date: row.reporting_date,
                name: row.reporting_date,
              }))
            : [],
          total: searchQueryPerformanceTotalsReportQuery.data?.data,
        }}
        loading={
          searchQueryPerformanceTotalsReportQuery.isFetching &&
          !searchQueryPerformanceTotalsReportQuery.isPlaceholderData
        }
        showLoadingSpinner
        showSkeletonLoadingEffect={
          searchQueryPerformanceTotalsReportQuery.isFetching &&
          !searchQueryPerformanceTotalsReportQuery.isPlaceholderData
        }
        metrics={metrics}
        showMetricsSelector
        initialSelectedMetric={["search_query_score", "search_query_volume", "impressions_total", "clicks_total"]}
        group={"day"}
        showAnnotations={false}
        byline={
          <>
            {moment(start).format("MMMM Do YYYY")}
            {" - "}
            {moment(end).endOf("week").format("MMMM Do YYYY")}
          </>
        }
        context={"searchQuery"}
        inlineStatus={
          <Flex className={css({ gap: "small", alignItems: "center", paddingX: "large", whiteSpace: "nowrap" })}>
            <Button
              variant={"link"}
              className={css({ paddingStart: "large", fontSize: "xl" })}
              onPress={() => history.push("/user/searchqueryperformance")}
            >
              View Uploaded Reports
            </Button>
          </Flex>
        }
      >
        <Modal
          isOpen={showAsinFilter}
          onOpenChange={(isOpen) => !isOpen && setShowAsinFilter(false)}
          title={"Select ASINs to Filter Stats"}
          className={css({ width: "55vw", minHeight: "600px", padding: "large", overflowY: "auto" })}
        >
          <ProductSelector
            selectedRowIds={tempSelectedAsins.map((asin) => `${asin.product_id}:${asin.asin}:${asin.sku}`)}
            onSelectionChange={(selectedProducts: string[]) => {
              setTempSelectedAsins(
                selectedProducts.map((productKey) => {
                  const [productId, asin, sku] = productKey.split(":");
                  return {
                    product_id: String(productId),
                    asin: String(asin),
                    sku: String(sku),
                  };
                }),
              );
            }}
            context={`searchQueryPerformanceDashboardProductSelector`}
            enableRowSelection={true}
            enableSelectAll={false}
            rowIdAccessor={"product_key"}
          />
          <Flex gap={"large"} justifyContent={"center"} marginTop={"auto"}>
            <Button
              variant="secondary"
              onPress={() => {
                setShowAsinFilter(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onPress={() => {
                setSelectedAsins(tempSelectedAsins);
                setShowAsinFilter(false);
              }}
            >
              Confirm
            </Button>
          </Flex>
        </Modal>

        <SearchQueryPerformanceDashboardTable
          state={state}
          initialState={initialState}
          setters={setters}
          setShowAsinFilter={(value) => {
            if (value) {
              setTempSelectedAsins([...selectedAsins]);
            }
            setShowAsinFilter(value);
          }}
          // selectedAsins={selectedAsins}
          selectedAsins={[]}
          setSelectedAsins={setSelectedAsins}
          columns={columns}
        />
      </MetricsCard>
    </PageContainer>
  );
};

export default function SQPRReportV2() {
  const breadCrumbItems = [
    {
      name: "Search Query Performance",
      href: "/user/report/sqpr_report",
      dropdownLinkOptions: REPORT_LINKS,
      searchable: false,
      dropdownSubtitle: "Reports",
    },
  ];

  return (
    <DatesProvider>
      <BreadcrumbDates items={breadCrumbItems} hideCompare={true} hideGroupBy={true} />
      <SQPRReportBody />
    </DatesProvider>
  );
}
